import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select'; // Import SelectChangeEvent
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import LinkButton from '../../../LinkButton'; // Importing LinkButton

import {
  getCartItems,
  deleteProductFromCart,
  updateCartQuantity,
} from '../../CartService';

const Orders = (): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); // State for confirmation dialog
  const [productToRemove, setProductToRemove] = useState<string | null>(null);
  const [itemToUpdate, setItemToUpdate] = useState<any | null>(null); // Store item being updated
  const [tempQuantity, setTempQuantity] = useState<number | null>(null); // Temporary quantity before saving

  useEffect(() => {
    listCartItems();
  }, []);

  const listCartItems = () => {
    setLoading(true);
    getCartItems()
      .then((response) => setCartItems(response.data))
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  const handleQuantityChange = (item: any) => {
    setItemToUpdate(item); // Store item being updated for the confirmation dialog
    setOpenConfirmDialog(true); // Open the confirmation dialog when quantity is changed
  };

  const handleRemoveClick = (productId: string) => {
    setProductToRemove(productId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setProductToRemove(null);
  };

  const handleConfirmRemove = () => {
    if (productToRemove) {
      deleteProductFromCart(productToRemove)
        .then(() => {
          setCartItems((prevItems) =>
            prevItems.filter((item) => item.product.id !== productToRemove),
          );
          navigate('/ecommerce/cart');
        })
        .catch((error) => console.error('Error removing product:', error))
        .finally(() => handleCloseModal());
    }
  };

  const handleSave = () => {
    if (itemToUpdate && tempQuantity !== null) {
      // Update the quantity of the selected item in the state
      const updatedCartItems = cartItems.map((item) =>
        item.product.id === itemToUpdate.product.id
          ? { ...item, quantity: tempQuantity }
          : item,
      );
      setCartItems(updatedCartItems);
      updateCartQuantity(itemToUpdate.product.id, tempQuantity)
        .then(() => {
          console.log('Quantity updated successfully');
          navigate('/ecommerce/cart');
        })
        .catch((error) => {
          console.error('Error updating quantity:', error);
        });

      setOpenConfirmDialog(false); // Close dialog after confirming
    }
  };

  const handleCancelSave = () => {
    setOpenConfirmDialog(false); // Close dialog without saving
  };

  // Handle the quantity change
  const handleTempQuantityChange = (e: SelectChangeEvent<unknown>) => {
    console.log(e.target);
    const value = e.target.value as number;
    setTempQuantity(value); // Update temporary quantity as user selects a new value
  };

  return (
    <Box>
      {loading && <p>Loading...</p>}

      {cartItems.length === 0 && !loading && (
        <Typography
          variant="h6"
          color="text.secondary"
          align="center"
          sx={{ marginTop: 3 }}
        >
          Your cart is empty. Start adding items to your cart!
        </Typography>
      )}

      {cartItems.map((item, i) => (
        <Box key={i}>
          <Box display={'flex'}>
            <Box
              component={'img'}
              src={'/assets/favicon.ico'}
              alt={item.title}
              sx={{
                borderRadius: 2,
                width: 1,
                height: 1,
                maxWidth: { xs: 120, sm: 200 },
                marginRight: 2,
                filter:
                  theme.palette.mode === 'dark' ? 'brightness(0.7)' : 'none',
              }}
            />
            <Box
              display={'flex'}
              flexDirection={{ xs: 'column', sm: 'row' }}
              justifyContent={'space-between'}
              alignItems={'flex-start'}
              width={1}
            >
              <Box sx={{ order: 1 }}>
                <Typography fontWeight={700} gutterBottom>
                  {item.product.productName}
                </Typography>
                <Typography fontWeight={700} gutterBottom>
                  {item.product.productCost === 0
                    ? 'Free'
                    : `£${item.product.productCost.toFixed(2)}`}
                </Typography>
                <Typography
                  color={'text.secondary'}
                  variant={'subtitle2'}
                  gutterBottom
                >
                  Type:{' '}
                  <Typography variant={'inherit'} fontWeight={700}>
                    {item.product.productType}
                  </Typography>
                </Typography>
                <Typography
                  color={'text.secondary'}
                  variant={'subtitle2'}
                  gutterBottom
                >
                  Duration:{' '}
                  <Typography variant={'inherit'} fontWeight={700}>
                    {item.product.costFrequencyUnit}
                  </Typography>
                </Typography>
                <Typography
                  color={'text.secondary'}
                  variant={'subtitle2'}
                  noWrap
                  gutterBottom
                >
                  Code:{' '}
                  <Typography variant={'inherit'} fontWeight={700}>
                    {item.product.apps[0].appCode} (
                    {item.product.apps[0].appName})
                  </Typography>
                </Typography>
              </Box>

              <Stack
                spacing={1}
                direction={{ xs: 'row', sm: 'column' }}
                marginTop={{ xs: 2, sm: 0 }}
                sx={{ order: { xs: 3, sm: 2 } }}
              >
                <LinkButton
                  text="Remove"
                  onClick={() => handleRemoveClick(item.product.id)}
                />
                <LinkButton
                  text="Save"
                  onClick={() => handleQuantityChange(item)} // Trigger quantity change for selected item
                />
              </Stack>

              <Stack
                spacing={1}
                direction={'row'}
                alignItems={'center'}
                sx={{ order: { xs: 2, sm: 3 } }}
              >
                <FormControl fullWidth>
                  <Select
                    value={tempQuantity || item.quantity || 1} // Ensure tempQuantity is used when available
                    onChange={handleTempQuantityChange} // Handle quantity change
                    sx={{
                      '& .MuiSelect-select': { paddingY: 0.5 },
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                      <MenuItem key={i} value={i}>
                        {i}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography fontWeight={700} marginLeft={2}>
                  {item.price}
                </Typography>
              </Stack>
            </Box>
          </Box>
          {i !== cartItems.length - 1 && <Divider sx={{ marginY: 2 }} />}
        </Box>
      ))}

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this item from your cart?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="secondary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for quantity change */}
      <Dialog open={openConfirmDialog} onClose={handleCancelSave}>
        <DialogTitle>Confirm Quantity Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to save the new quantity for{' '}
            {itemToUpdate?.product.productName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="secondary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
